import { countMinMediaEligible, isMinMediaSatisfied } from "@instacover/helpers"
import { useLiveQuery } from "dexie-react-hooks"

import { PhotoType } from "@/utils/types"

import { getClaimDetailsBySession } from "../tables/claimDetails/claimDetails"
import { getClaimMarkersBySession } from "../tables/claimMarkers/claimMarkers"
import { findOrderedSteps, isRealOrderedStep, type RealOrderedStep } from "../tables/orderedSteps/orderedSteps"
import { findAllPhotosForSession, getUnsyncedPhotoDeletions } from "../tables/photos/photos"
import { findAllVideosForSession } from "../tables/videos/videos"
import type { LiveQueryResult } from "../types"

export type UploadStatus = {
  hasFailedPhotos: boolean
  hasUploadingPhotos: boolean
  hasAllUploaded: boolean
  stepsWithMissingPhotos: RealOrderedStep[]
  hasUploadingVideos: boolean
  hasFailedVideos: boolean
  stepsWithMissingVideos: RealOrderedStep[]
  hasUploadingClaim: boolean
  hasFailedClaim: boolean
  hasIncompleteClaimStep: boolean
}

/** Upload status derived from Dexie — queries photos/videos directly */
export function useUploadStatus(sessionId: string): LiveQueryResult<UploadStatus> {
  return useLiveQuery(
    async () => {
      const [orderedSteps, allPhotos, photoDeletions, allVideos, allClaimMarkers, allClaimDetails] = await Promise.all([
        findOrderedSteps(sessionId),
        findAllPhotosForSession(sessionId),
        getUnsyncedPhotoDeletions(sessionId),
        findAllVideosForSession(sessionId),
        getClaimMarkersBySession(sessionId, true),
        getClaimDetailsBySession(sessionId, true),
      ])

      const realSteps = orderedSteps.filter(isRealOrderedStep)

      // Pending photos = not uploaded, not skipped, not deleted, a photo deleted (locally) after it was uploaded
      const pendingPhotos = allPhotos.filter((p) => !p.uploadedAt && !p.skipped && !p.deleted)
      const hasFailedPhotos = pendingPhotos.some((p) => p.uploadFailed) || photoDeletions.some((p) => p.deleteFailed)
      const hasUploadingPhotos = pendingPhotos.length > 0 || photoDeletions.length > 0

      const stepsWithoutPhotos: PhotoType[] = ["ExtraClientData", "DashVideo", "MotorVideo", "OdometerVideo"]

      const stepsWithMissingPhotos = realSteps.filter((step) => {
        if (step.minMedia === 0) return false
        if (stepsWithoutPhotos.includes(step.name)) return false
        const stepPhotos = allPhotos.filter((p) => p.type === step.name && !p.deleted)
        return !isMinMediaSatisfied(step.minMedia, countMinMediaEligible(stepPhotos))
      })

      const stepsWithMissingVideos = realSteps.filter((step) => {
        if (step.stepType !== "VehicleVideo") return false
        if (step.minMedia === 0) return false
        const stepVideos = allVideos.filter((v) => v.type === step.name)
        return !isMinMediaSatisfied(step.minMedia, countMinMediaEligible(stepVideos)) && !step.isFinished
      })

      // Pending videos = not uploaded
      const pendingVideos = allVideos.filter((v) => v.uploaded === 0)
      const hasUploadingVideos = pendingVideos.length > 0
      const hasFailedVideos = pendingVideos.some((v) => v.uploadFailed)

      // Claim markers/details still needing an upload (not synced, not deleted) or a delete-sync
      // (deleted locally but not synced to server - successful delete-sync hard-deletes the local row).
      const claimRows = [...allClaimMarkers, ...allClaimDetails]
      const pendingClaimUploads = claimRows.filter((row) => !row.uploadedAt && !row.deleted)
      const pendingClaimDeletions = claimRows.filter((row) => row.deleted && !!row.uploadedAt)
      const hasUploadingClaim = pendingClaimUploads.length > 0 || pendingClaimDeletions.length > 0
      const hasFailedClaim =
        pendingClaimUploads.some((row) => row.uploadFailed) || pendingClaimDeletions.some((row) => row.deleteFailed)

      // Claim step is mandatory: a present Claim step with no (non-deleted) markers blocks finalizing
      const hasClaimStep = realSteps.some((step) => step.stepType === "Claim")
      const activeClaimMarkers = allClaimMarkers.filter((marker) => !marker.deleted)
      const hasIncompleteClaimStep = hasClaimStep && activeClaimMarkers.length === 0

      const hasAllUploaded =
        !hasUploadingPhotos &&
        !hasUploadingVideos &&
        !hasUploadingClaim &&
        stepsWithMissingPhotos.length === 0 &&
        stepsWithMissingVideos.length === 0

      return {
        data: {
          hasFailedPhotos,
          hasUploadingPhotos,
          hasAllUploaded,
          stepsWithMissingPhotos,
          hasUploadingVideos,
          hasFailedVideos,
          stepsWithMissingVideos,
          hasUploadingClaim,
          hasFailedClaim,
          hasIncompleteClaimStep,
        },
        isLoading: false,
      }
    },
    [sessionId],
    { data: undefined, isLoading: true },
  )
}
